import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  FormControl,
  InputLabel,
  TextField,
  Button,
  MenuItem,
  Select,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "components/Toast";
import { createVehiclePrice, getAreas, getStates } from "services/vehicle";

const schema = yup.object().shape({
  state: yup.string().required("State is required"),
  price: yup.number().required("Price is required").min(0),
  type: yup.string().oneOf(["area", "distance"]).required("Type is required"),
  area: yup.string().when("type", {
    is: "area",
    then: (schema) => schema.required("Area is required"),
  }),
  minduration: yup.number().when("type", {
    is: "distance",
    then: (schema) => schema.required("Minimum duration is required").min(1),
  }),
  maxduration: yup.number().when("type", {
    is: "distance",
    then: (schema) =>
      schema
        .required("Maximum duration is required")
        .min(1)
        .test(
          "max",
          "Maximum duration must be greater than minimum duration",
          function (value) {
            const { minduration } = this.parent;
            return !minduration || !value || value > minduration;
          }
        ),
  }),
});

interface PriceFormProps {
  vehicleId: string;
  onSuccess: () => void;
}

const PriceForm = ({ vehicleId, onSuccess }: PriceFormProps) => {
  const { showToast } = useToast();

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: getStates,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: "area",
      price: 0,
      state: "",
      area: "",
      minduration: 0,
      maxduration: 0,
    },
  });

  const selectedState = watch("state");
  const { data: areas } = useQuery({
    queryKey: ["areas", selectedState],
    queryFn: () => getAreas(selectedState),
    enabled: Boolean(selectedState),
  });

  const priceMutation = useMutation({
    mutationFn: createVehiclePrice,
    onSuccess: () => {
      showToast("Price added successfully!", "success");
      onSuccess();
    },
    onError: () => {
      showToast("Failed to add price", "error");
    },
  });

  const priceType = watch("type");

  const onSubmit = (data: any) => {
    const submitData = {
      ...data,
      car_id: vehicleId,
    };

    // Remove irrelevant fields based on type
    if (data.type === "distance") {
      delete submitData.area;
    } else {
      delete submitData.minduration;
      delete submitData.maxduration;
    }

    priceMutation.mutate(submitData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 2 }}>
        <FormControl>
          <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
            State
          </InputLabel>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                variant="standard"
                sx={{
                  mt: 2,
                  bgcolor: "#F9F9F9",
                  fontSize: "14px",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                }}
                error={!!errors.state}
                displayEmpty
                value={field.value || ""}
                renderValue={(selected) => {
                  if (!selected) return "Select a state";
                  const state = states?.data?.find(
                    (s: any) => s._id === selected
                  );
                  return state?.name || "Select a state";
                }}
              >
                {states?.data?.map((state: any) => (
                  <MenuItem key={state._id} value={state._id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>

        <FormControl>
          <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
            Price Type
          </InputLabel>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                variant="standard"
                sx={{
                  mt: 2,
                  bgcolor: "#F9F9F9",
                  fontSize: "14px",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                }}
              >
                <MenuItem value="area">Area Based</MenuItem>
                <MenuItem value="distance">Distance Based</MenuItem>
              </Select>
            )}
          />
        </FormControl>

        {priceType === "area" && (
          <FormControl>
            <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
              Area
            </InputLabel>
            <Controller
              name="area"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  variant="standard"
                  sx={{
                    mt: 2,
                    bgcolor: "#F9F9F9",
                    fontSize: "14px",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                  }}
                  error={!!errors.area}
                  displayEmpty
                  value={field.value || ""}
                  renderValue={(selected) => {
                    if (!selected) return "Select an area";
                    const area = areas?.data?.find(
                      (a: any) => a._id === selected
                    );
                    return area?.name || "Select an area";
                  }}
                >
                  {areas?.data?.map((area: any) => (
                    <MenuItem key={area._id} value={area._id}>
                      {area.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        )}

        {priceType === "distance" && (
          <>
            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Minimum Duration (minutes)
              </InputLabel>
              <Controller
                name="minduration"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    variant="standard"
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                    error={!!errors.minduration}
                    helperText={errors.minduration?.message}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
                Maximum Duration (minutes)
              </InputLabel>
              <Controller
                name="maxduration"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    variant="standard"
                    sx={{
                      mt: 2,
                      bgcolor: "#F9F9F9",
                      fontSize: "14px",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                    }}
                    error={!!errors.maxduration}
                    helperText={errors.maxduration?.message}
                  />
                )}
              />
            </FormControl>
          </>
        )}

        <FormControl>
          <InputLabel shrink sx={{ color: "#344054", fontWeight: 600 }}>
            Price
          </InputLabel>
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                variant="standard"
                sx={{
                  mt: 2,
                  bgcolor: "#F9F9F9",
                  fontSize: "14px",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                }}
                error={!!errors.price}
                helperText={errors.price?.message}
              />
            )}
          />
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          disabled={priceMutation.isPending}
          sx={{
            mt: 2,
            bgcolor: "#379E66",
            color: "white",
            py: 1,
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#2b7d4f",
            },
          }}
        >
          {priceMutation.isPending ? "Adding..." : "Add Price"}
        </Button>
      </Box>
    </form>
  );
};

export default PriceForm;
